// src/services/creatorCrmService.js

import { apiRequest } from "./apiService";

function norm(value) {
  return String(value ?? "").trim();
}

function stripAt(value) {
  return norm(value).replace(/^@+/, "");
}

function enc(value) {
  return encodeURIComponent(norm(value));
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object || {}, key);
}

function isObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function buildQueryString(params = {}) {
  const search = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    const safeValue = Array.isArray(value)
      ? value.filter((item) => norm(item)).join(",")
      : norm(value);

    if (!safeValue) return;
    search.set(key, safeValue);
  });

  const query = search.toString();
  return query ? `?${query}` : "";
}

function omitCampaignId(params = {}) {
  const next = { ...(params || {}) };
  delete next.campaignId;
  return next;
}

function unwrapResponseData(payload) {
  if (
    payload &&
    typeof payload === "object" &&
    payload.data &&
    typeof payload.data === "object"
  ) {
    return payload.data;
  }

  return payload;
}

function normalizeCrmStage(value) {
  const stage = norm(value).toLowerCase();

  if (["", "draft", "shortlisted"].includes(stage)) return "new";
  if (["sent", "invited"].includes(stage)) return "contacted";
  if (stage === "accepted") return "replied";

  if (["declined", "rejected", "lost", "archived"].includes(stage)) {
    return "not_now";
  }

  if (
    [
      "new",
      "contacted",
      "replied",
      "negotiating",
      "scheduled",
      "live",
      "completed",
      "not_now",
      "paused",
    ].includes(stage)
  ) {
    return stage;
  }

  return "new";
}

/**
 * The campaign API commonly identifies a creator as id:{id}, while the CRM
 * API stores the same creator as influencer:{id}. Convert both forms to the
 * CRM API's canonical key so create/update detection remains stable.
 */
export function canonicalCreatorCrmKey(valueOrRow) {
  const row = isObject(valueOrRow) ? valueOrRow : {};
  const identity = isObject(row.identity) ? row.identity : {};

  const explicit = norm(
    typeof valueOrRow === "string" ? valueOrRow : row.creatorKey
  );

  if (explicit) {
    const separatorIndex = explicit.indexOf(":");

    if (separatorIndex > 0) {
      const prefix = explicit.slice(0, separatorIndex).toLowerCase();
      const rawValue = norm(explicit.slice(separatorIndex + 1));

      if (!rawValue) return "";

      if (["influencer", "creator", "id"].includes(prefix)) {
        return `influencer:${rawValue.toLowerCase()}`;
      }

      if (["handle", "username"].includes(prefix)) {
        const handle = stripAt(rawValue).toLowerCase();
        return handle ? `handle:${handle}` : "";
      }

      return `${prefix}:${rawValue}`;
    }
  }

  const influencerId = norm(
    identity.influencerId ||
      identity.creatorId ||
      row.influencerId ||
      row.creatorId ||
      row.id ||
      row._id
  );

  if (influencerId) {
    return `influencer:${influencerId.toLowerCase()}`;
  }

  const handle = stripAt(
    identity.handle ||
      identity.username ||
      row.handle ||
      row.username ||
      row.creatorHandle ||
      row.crmHandle
  ).toLowerCase();

  return handle ? `handle:${handle}` : "";
}

function normalizeStringList(value) {
  if (!Array.isArray(value)) return [];

  return Array.from(
    new Set(value.map((item) => norm(item)).filter(Boolean))
  );
}

export function normalizeCreatorCrmRecord(raw = {}) {
  const row = isObject(raw) ? raw : {};
  const identity = isObject(row.identity) ? row.identity : {};
  const metadata = isObject(row.metadata) ? row.metadata : {};

  const creatorKey = canonicalCreatorCrmKey(row);

  const creatorId = norm(
    identity.creatorId ||
      identity.influencerId ||
      row.creatorId ||
      row.influencerId
  );

  const influencerId = norm(
    identity.influencerId ||
      identity.creatorId ||
      row.influencerId ||
      row.creatorId
  );

  const username = stripAt(
    identity.username ||
      identity.handle ||
      row.username ||
      row.handle
  );

  const handle = stripAt(
    identity.handle ||
      identity.username ||
      row.handle ||
      row.username
  );

  const linkedOfferIds = normalizeStringList(
    row.linkedOfferIds ||
      row.linked_offer_ids ||
      (norm(row.offerId || row.crmOfferId)
        ? [norm(row.offerId || row.crmOfferId)]
        : [])
  );

  const linkedInviteIds = normalizeStringList(
    row.linkedInviteIds || row.linked_invite_ids || []
  );

  const latestNote = norm(
    row.latestNote ||
      row.latest_note ||
      row.notes ||
      row.notesPreview
  );

  const nextStep = norm(
    metadata.nextStep ||
      metadata.next_step ||
      row.nextStep ||
      row.nextAction
  );

  const stage = normalizeCrmStage(
    row.status ||
      row.crmStage ||
      row.crmStatus ||
      row.stage
  );

  return {
    ...row,
    identity: {
      ...identity,
      creatorId,
      influencerId,
      username,
      handle,
      displayName: norm(
        identity.displayName ||
          row.displayName ||
          identity.username ||
          row.username
      ),
      platform: norm(identity.platform || row.platform).toLowerCase(),
      profileUrl: norm(identity.profileUrl || row.profileUrl),
      email: norm(identity.email || row.email),
    },
    metadata: {
      ...metadata,
      nextStep,
    },

    creatorKey,
    creatorId,
    influencerId,
    username,
    handle,
    displayName: norm(
      identity.displayName ||
        row.displayName ||
        identity.username ||
        row.username
    ),
    platform: norm(identity.platform || row.platform).toLowerCase(),
    profileUrl: norm(identity.profileUrl || row.profileUrl),
    email: norm(identity.email || row.email),

    status: stage,
    crmStage: stage,
    crmStatus: stage,

    latestNote,
    notes: latestNote,
    notesPreview: latestNote,

    nextStep,
    nextAction: nextStep,

    lastContactedAt: norm(
      row.lastContactedAt ||
        row.last_contacted_at ||
        row.lastTouchedAt
    ),
    lastTouchedAt: norm(
      row.lastTouchedAt ||
        row.lastContactedAt ||
        row.last_contacted_at
    ),

    linkedOfferIds,
    linkedInviteIds,
    offerId: linkedOfferIds[0] || "",
    crmOfferId: linkedOfferIds[0] || "",
  };
}

function normalizeListResponse(payload) {
  const body = unwrapResponseData(payload);

  if (Array.isArray(body)) {
    const items = body.map(normalizeCreatorCrmRecord);

    return {
      items,
      total: items.length,
      count: items.length,
      cursor: "",
      nextCursor: "",
      lastKey: "",
      hasMore: false,
      summary: {},
      unavailable: false,
    };
  }

  if (!body || typeof body !== "object") {
    return {
      items: [],
      total: 0,
      count: 0,
      cursor: "",
      nextCursor: "",
      lastKey: "",
      hasMore: false,
      summary: {},
      unavailable: false,
    };
  }

  const rawItems =
    body.items ||
    body.results ||
    body.creators ||
    body.rows ||
    body.records ||
    body.activities ||
    [];

  const items = Array.isArray(rawItems)
    ? rawItems.map(normalizeCreatorCrmRecord)
    : [];

  const cursor = norm(
    body.nextCursor ||
      body.next_cursor ||
      body.cursor ||
      body.lastKey ||
      body.last_key ||
      body.nextKey
  );

  const total =
    Number(body.total ?? body.count ?? items.length) || items.length;

  return {
    ...body,
    items,
    total,
    count: total,
    cursor,
    nextCursor: cursor,
    lastKey: cursor,
    hasMore:
      typeof body.hasMore === "boolean"
        ? body.hasMore
        : Boolean(cursor),
    summary: isObject(body.summary) ? body.summary : {},
    unavailable: Boolean(body.unavailable),
  };
}

function normalizeSingleResponse(payload) {
  const body = unwrapResponseData(payload);

  if (!body || typeof body !== "object") {
    return body;
  }

  const record =
    (isObject(body.item) && body.item) ||
    (isObject(body.creator) && body.creator) ||
    (isObject(body.record) && body.record) ||
    (isObject(body.activity) && body.activity) ||
    body;

  return normalizeCreatorCrmRecord(record);
}

function assertCampaignId(campaignId) {
  const safeCampaignId = enc(campaignId);

  if (!safeCampaignId) {
    throw new Error("campaignId is required.");
  }

  return safeCampaignId;
}

function assertCreatorKey(creatorKey) {
  const canonicalKey = canonicalCreatorCrmKey(creatorKey);

  if (!canonicalKey) {
    throw new Error("creatorKey is required.");
  }

  return enc(canonicalKey);
}

function buildCampaignCrmBasePath(campaignId) {
  const safeCampaignId = assertCampaignId(campaignId);
  return `/campaigns/${safeCampaignId}/creator-crm`;
}

function resolveOfferIds(payload = {}) {
  if (Array.isArray(payload.linkedOfferIds)) {
    return normalizeStringList(payload.linkedOfferIds);
  }

  const offerId = norm(
    payload.offerId ||
      payload.crmOfferId ||
      payload.preferredOfferId
  );

  return offerId ? [offerId] : [];
}

function buildIdentity(payload = {}) {
  const sourceIdentity = isObject(payload.identity) ? payload.identity : {};

  const creatorId = norm(
    sourceIdentity.creatorId ||
      sourceIdentity.influencerId ||
      payload.creatorId ||
      payload.influencerId
  );

  const influencerId = norm(
    sourceIdentity.influencerId ||
      sourceIdentity.creatorId ||
      payload.influencerId ||
      payload.creatorId
  );

  const username = stripAt(
    sourceIdentity.username ||
      sourceIdentity.handle ||
      payload.username ||
      payload.handle
  );

  const handle = stripAt(
    sourceIdentity.handle ||
      sourceIdentity.username ||
      payload.handle ||
      payload.username
  );

  return {
    creatorId,
    influencerId,
    username,
    handle,
    displayName: norm(
      sourceIdentity.displayName ||
        payload.displayName ||
        payload.name ||
        username
    ),
    platform: norm(
      sourceIdentity.platform ||
        payload.platform ||
        "instagram"
    ).toLowerCase(),
    profileUrl: norm(
      sourceIdentity.profileUrl ||
        payload.profileUrl
    ),
    email: norm(sourceIdentity.email || payload.email),
  };
}

/**
 * Converts the drawer/UI model into the deployed CRM PUT contract.
 */
export function buildCreatorCrmCreatePayload(payload = {}) {
  const stage = normalizeCrmStage(
    payload.status ||
      payload.crmStage ||
      payload.crmStatus ||
      payload.stage ||
      "new"
  );

  const metadata = {
    ...(isObject(payload.metadata) ? payload.metadata : {}),
    nextStep: norm(
      payload.nextStep ||
        payload.nextAction ||
        payload.metadata?.nextStep
    ),
  };

  return {
    identity: buildIdentity(payload),
    status: stage,
    priority: norm(payload.priority || "medium").toLowerCase(),
    outreachChannel: norm(
      payload.outreachChannel || payload.outreach_channel || "instagram_dm"
    ).toLowerCase(),
    latestNote: norm(
      payload.latestNote ||
        payload.notes ||
        payload.notesPreview
    ),
    statusReason: norm(payload.statusReason),
    tags: normalizeStringList(payload.tags),
    linkedOfferIds: resolveOfferIds(payload),
    linkedInviteIds: normalizeStringList(payload.linkedInviteIds),
    nextFollowUpAt: norm(
      payload.nextFollowUpAt ||
        payload.next_follow_up_at
    ),
    lastContactedAt: norm(
      payload.lastContactedAt ||
        payload.lastTouchedAt
    ),
    metadata,
  };
}

/**
 * Converts the drawer/UI model into the deployed CRM PATCH contract.
 * Explicit empty values are retained so users can clear saved fields.
 */
export function buildCreatorCrmPatchPayload(payload = {}) {
  const result = {};

  if (
    hasOwn(payload, "status") ||
    hasOwn(payload, "crmStage") ||
    hasOwn(payload, "crmStatus") ||
    hasOwn(payload, "stage")
  ) {
    result.status = normalizeCrmStage(
      payload.status ||
        payload.crmStage ||
        payload.crmStatus ||
        payload.stage
    );
  }

  if (
    hasOwn(payload, "latestNote") ||
    hasOwn(payload, "notes") ||
    hasOwn(payload, "notesPreview")
  ) {
    result.latestNote = norm(
      payload.latestNote ||
        payload.notes ||
        payload.notesPreview
    );
  }

  if (
    hasOwn(payload, "lastContactedAt") ||
    hasOwn(payload, "lastTouchedAt")
  ) {
    result.lastContactedAt = norm(
      payload.lastContactedAt ||
        payload.lastTouchedAt
    );
  }

  if (
    hasOwn(payload, "linkedOfferIds") ||
    hasOwn(payload, "offerId") ||
    hasOwn(payload, "crmOfferId") ||
    hasOwn(payload, "preferredOfferId")
  ) {
    result.linkedOfferIds = resolveOfferIds(payload);
  }

  if (hasOwn(payload, "linkedInviteIds")) {
    result.linkedInviteIds = normalizeStringList(payload.linkedInviteIds);
  }

  if (
    hasOwn(payload, "nextStep") ||
    hasOwn(payload, "nextAction") ||
    hasOwn(payload, "metadata")
  ) {
    result.metadata = {
      ...(isObject(payload.metadata) ? payload.metadata : {}),
      nextStep: norm(
        payload.nextStep ||
          payload.nextAction ||
          payload.metadata?.nextStep
      ),
    };
  }

  if (hasOwn(payload, "priority")) {
    result.priority = norm(payload.priority).toLowerCase();
  }

  if (
    hasOwn(payload, "outreachChannel") ||
    hasOwn(payload, "outreach_channel")
  ) {
    result.outreachChannel = norm(
      payload.outreachChannel || payload.outreach_channel
    ).toLowerCase();
  }

  if (hasOwn(payload, "statusReason")) {
    result.statusReason = norm(payload.statusReason);
  }

  if (hasOwn(payload, "tags")) {
    result.tags = normalizeStringList(payload.tags);
  }

  if (
    hasOwn(payload, "nextFollowUpAt") ||
    hasOwn(payload, "next_follow_up_at")
  ) {
    result.nextFollowUpAt = norm(
      payload.nextFollowUpAt ||
        payload.next_follow_up_at
    );
  }

  return result;
}

function mergeMutationFallback({
  creatorKey,
  requestPayload,
  response,
}) {
  const normalizedResponse =
    response && typeof response === "object"
      ? normalizeSingleResponse(response)
      : null;

  const responseLooksLikeRecord = Boolean(
    normalizedResponse &&
      (
        normalizedResponse.identity ||
        normalizedResponse.creatorKey ||
        normalizedResponse.crmRecordId ||
        normalizedResponse.status
      )
  );

  if (responseLooksLikeRecord) {
    return normalizeCreatorCrmRecord({
      creatorKey: canonicalCreatorCrmKey(creatorKey),
      ...requestPayload,
      ...normalizedResponse,
    });
  }

  return normalizeCreatorCrmRecord({
    creatorKey: canonicalCreatorCrmKey(creatorKey),
    ...requestPayload,
    ...(isObject(response) ? response : {}),
  });
}

export async function listCreatorCrm({
  campaignId,
  token: _token,
  params = {},
} = {}) {
  const safeParams = omitCampaignId(params);

  const endpoint = `${buildCampaignCrmBasePath(campaignId)}${buildQueryString(
    safeParams
  )}`;

  const payload = await apiRequest(endpoint, "GET", null, {
    requiresAuth: true,
    timeoutMs: 15000,
  });

  return normalizeListResponse(payload);
}

export async function getCreatorCrm({
  campaignId,
  creatorKey,
  token: _token,
} = {}) {
  const safeCreatorKey = assertCreatorKey(creatorKey);

  const payload = await apiRequest(
    `${buildCampaignCrmBasePath(campaignId)}/${safeCreatorKey}`,
    "GET",
    null,
    {
      requiresAuth: true,
      timeoutMs: 15000,
    }
  );

  return normalizeSingleResponse(payload);
}

export async function createCreatorCrm({
  campaignId,
  creatorKey,
  token: _token,
  payload = {},
} = {}) {
  const canonicalKey = canonicalCreatorCrmKey(creatorKey || payload);
  const safeCreatorKey = assertCreatorKey(canonicalKey);
  const requestPayload = buildCreatorCrmCreatePayload(payload);

  const result = await apiRequest(
    `${buildCampaignCrmBasePath(campaignId)}/${safeCreatorKey}`,
    "PUT",
    requestPayload,
    {
      requiresAuth: true,
      timeoutMs: 15000,
    }
  );

  return mergeMutationFallback({
    creatorKey: canonicalKey,
    requestPayload,
    response: result,
  });
}

export async function updateCreatorCrm({
  campaignId,
  creatorKey,
  token: _token,
  payload = {},
} = {}) {
  const canonicalKey = canonicalCreatorCrmKey(creatorKey || payload);
  const safeCreatorKey = assertCreatorKey(canonicalKey);
  const requestPayload = buildCreatorCrmPatchPayload(payload);

  const result = await apiRequest(
    `${buildCampaignCrmBasePath(campaignId)}/${safeCreatorKey}`,
    "PATCH",
    requestPayload,
    {
      requiresAuth: true,
      timeoutMs: 15000,
    }
  );

  return mergeMutationFallback({
    creatorKey: canonicalKey,
    requestPayload,
    response: result,
  });
}

export async function createCreatorCrmActivity({
  campaignId,
  creatorKey,
  token: _token,
  payload = {},
} = {}) {
  const canonicalKey = canonicalCreatorCrmKey(creatorKey || payload);
  const safeCreatorKey = assertCreatorKey(canonicalKey);

  const result = await apiRequest(
    `${buildCampaignCrmBasePath(campaignId)}/${safeCreatorKey}/activities`,
    "POST",
    payload,
    {
      requiresAuth: true,
      timeoutMs: 15000,
    }
  );

  return unwrapResponseData(result);
}

/**
 * Lists the persisted interaction history for one creator CRM relationship.
 *
 * GET /campaigns/{campaignId}/creator-crm/{creatorKey}/activities
 */
export async function listCreatorCrmActivities({
  campaignId,
  creatorKey,
  token: _token,
  params = {},
} = {}) {
  const canonicalKey = canonicalCreatorCrmKey(creatorKey);
  const safeCreatorKey = assertCreatorKey(canonicalKey);
  const safeParams = omitCampaignId(params);

  const result = await apiRequest(
    `${buildCampaignCrmBasePath(campaignId)}/${safeCreatorKey}/activities${buildQueryString(
      safeParams
    )}`,
    "GET",
    null,
    {
      requiresAuth: true,
      timeoutMs: 15000,
    }
  );

  const body = unwrapResponseData(result);

  if (Array.isArray(body)) {
    return {
      items: body,
      total: body.length,
      count: body.length,
      cursor: "",
      nextCursor: "",
      lastKey: "",
      hasMore: false,
      unavailable: false,
    };
  }

  if (!isObject(body)) {
    return {
      items: [],
      total: 0,
      count: 0,
      cursor: "",
      nextCursor: "",
      lastKey: "",
      hasMore: false,
      unavailable: false,
    };
  }

  const rawItems =
    body.items ||
    body.activities ||
    body.results ||
    [];

  const items = Array.isArray(rawItems) ? rawItems : [];

  const cursor = norm(
    body.nextCursor ||
      body.next_cursor ||
      body.cursor ||
      body.lastKey ||
      body.last_key
  );

  const parsedTotal = Number(
    body.total ??
      body.count ??
      items.length
  );

  const total = Number.isFinite(parsedTotal)
    ? Math.max(0, parsedTotal)
    : items.length;

  return {
    ...body,
    items,
    total,
    count: total,
    cursor,
    nextCursor: cursor,
    lastKey: cursor,
    hasMore:
      typeof body.hasMore === "boolean"
        ? body.hasMore
        : Boolean(cursor),
    unavailable: false,
  };
}
